const WEEKLY_METRICS = ['min', 'mean', 'max'];

const normalizeDate = (date) => {
  if (!date) return null;
  return String(date).split('T')[0] || null;
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getStateFips = (regionId) => {
  const normalized = String(regionId ?? '').trim();
  if (!normalized) return null;
  if (normalized.length <= 2) return normalized.padStart(2, '0');
  return normalized.padStart(5, '0').slice(0, 2);
};

const getStateTotal = (stateTotals, stateFips) => {
  const total = stateTotals?.[stateFips] ?? stateTotals?.[Number(stateFips)];
  const number = Number(total);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const createDailyAggregate = (date) => ({
  date,
  isCountyAggregated: false,
  values: { min: 0, mean: 0, max: 0 },
  hasValue: { min: false, mean: false, max: false },
  percentages: { min: null, mean: null, max: null }
});

const createStatePeaks = () => ({
  min: { absolutePeak: null, percentPeak: null },
  mean: { absolutePeak: null, percentPeak: null },
  max: { absolutePeak: null, percentPeak: null }
});

const shouldReplacePeak = (candidate, current, field) => {
  if (!current) return true;
  if (candidate[field] > current[field]) return true;
  return candidate[field] === current[field] && candidate.date < current.date;
};

export const buildWeeklyCustomerPeaks = (predictionData = {}, stateTotals = {}) => {
  const dailyByState = {};

  Object.entries(predictionData).forEach(([regionId, entries]) => {
    if (!Array.isArray(entries)) return;

    const stateFips = getStateFips(regionId);
    if (!stateFips) return;

    const isCountyKeyed = String(regionId).trim().length > 2;
    dailyByState[stateFips] ??= {};

    entries.forEach((entry) => {
      const date = normalizeDate(entry?.date);
      const customers = entry?.customers;
      if (!date || !customers) return;

      const daily = dailyByState[stateFips][date]
        ?? (dailyByState[stateFips][date] = createDailyAggregate(date));

      if (isCountyKeyed) daily.isCountyAggregated = true;

      WEEKLY_METRICS.forEach((metric) => {
        const value = toFiniteNumber(customers[metric]);
        if (value === null) return;

        daily.values[metric] += value;
        daily.hasValue[metric] = true;

        if (!isCountyKeyed) {
          const percentage = toFiniteNumber(customers[`${metric}_percent_out`]);
          if (percentage !== null) daily.percentages[metric] = percentage;
        }
      });
    });
  });

  return Object.entries(dailyByState).reduce((peaks, [stateFips, dailyRecords]) => {
    const statePeaks = createStatePeaks();
    const stateTotal = getStateTotal(stateTotals, stateFips);

    Object.values(dailyRecords)
      .sort((left, right) => left.date.localeCompare(right.date))
      .forEach((daily) => {
        WEEKLY_METRICS.forEach((metric) => {
          if (!daily.hasValue[metric]) return;

          const value = daily.values[metric];
          const percentOut = daily.isCountyAggregated
            ? (stateTotal ? (value / stateTotal) * 100 : null)
            : daily.percentages[metric];
          const candidate = { value, percentOut, date: daily.date };
          const metricPeaks = statePeaks[metric];

          if (shouldReplacePeak(candidate, metricPeaks.absolutePeak, 'value')) {
            metricPeaks.absolutePeak = candidate;
          }
          if (percentOut !== null && shouldReplacePeak(candidate, metricPeaks.percentPeak, 'percentOut')) {
            metricPeaks.percentPeak = candidate;
          }
        });
      });

    WEEKLY_METRICS.forEach((metric) => {
      statePeaks[metric].percentPeak ??= statePeaks[metric].absolutePeak;
    });

    peaks[stateFips] = statePeaks;
    return peaks;
  }, {});
};

export const getWeeklyCustomerPeak = (
  weeklyPeaks,
  stateFips,
  metric,
  peakKind = 'absolutePeak'
) => weeklyPeaks?.[getStateFips(stateFips)]?.[metric]?.[peakKind] ?? null;

export const getTopWeeklyCustomerStates = (weeklyPeaks, metric, limit = 5) => {
  const normalizedLimit = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 5;

  return Object.keys(weeklyPeaks || {})
    .map((stateFips) => {
      const peak = getWeeklyCustomerPeak(weeklyPeaks, stateFips, metric, 'absolutePeak');
      return peak ? { id: getStateFips(stateFips), ...peak } : null;
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (right.value !== left.value) return right.value - left.value;
      if (left.date !== right.date) return left.date.localeCompare(right.date);
      return left.id.localeCompare(right.id);
    })
    .slice(0, normalizedLimit);
};

export const formatPeakDate = (date) => {
  if (!date) return 'N/A';

  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(date);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(parsed);
};
